import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Keeps scroll position sane across client-side navigations.
 *
 * React Router deliberately does not manage scroll. Without this, navigating
 * from the bottom of a long page to a new route leaves the visitor halfway
 * down the new page. When the target URL includes a #hash, we scroll to that
 * element instead (used by the footer service deep links).
 */
export const ScrollManager = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      // Wait a frame so the destination route has rendered its content.
      const frame = requestAnimationFrame(() => {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Move focus for keyboard and screen-reader users without a visual jump.
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        } else {
          window.scrollTo({ top: 0, left: 0 });
        }
      });
      return () => cancelAnimationFrame(frame);
    }

    window.scrollTo({ top: 0, left: 0 });
    return undefined;
  }, [pathname, hash]);

  return null;
};

export default ScrollManager;
